import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  LinearProgress,
} from "@mui/material";
import { ArrowUpFromLine, ArrowDownToLine, Clock, Map as MapIcon } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";

import { toActions }    from "./helpers/toActions";
import { groupActions } from "./helpers/groupActions";
import { ActionListFooter } from "./ActionListFooter";
import { ReportProblemModal } from "./ReportProblemModal";
import { TodayStatsPanel } from "./TodayStatsPanel";

import { useDelivery }  from "../../../context/DeliveryContext";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtTime(val) {
  if (!val) return "—";
  const d = val instanceof Date ? val : new Date(val);
  return d.toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" });
}

function urgencyColor(time) {
  if (!time) return "text.primary";
  return time.getTime() < Date.now() ? "error.main" : "text.primary";
}

function groupKey(group) {
  return `${group.actions[0].deliveryId}-${group.kind}`;
}

function getGoogleMapsUrl(address) {
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

function BusTrackButton() {
  return (
    <Tooltip title="Seuraa bussia kartalla">
      <IconButton
        size="small"
        component="a"
        href="https://www.onnibus.com/bussit-kartalla"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: "warning.700", flexShrink: 0, display: "inline-flex", width: 32, height: 32 }}
      >
        <MapIcon size={18} />
      </IconButton>
    </Tooltip>
  );
}
function MapButton({ address }) {
  const mapUrl = getGoogleMapsUrl(address);
  if (!mapUrl) return null;
  return (
    <Tooltip title="Avaa kartalla">
      <IconButton
        size="small"
        component="a"
        href={mapUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: "primary.main", flexShrink: 0, display: "inline-flex", width: 32, height: 32 }}
      >
        <MapIcon size={18} />
      </IconButton>
    </Tooltip>
  );
}

// ─── primitives ───────────────────────────────────────────────────────────────

function KindPill({ kind, color }) {
  const isPickup = kind === "pickup";
  const Icon = isPickup ? ArrowUpFromLine : ArrowDownToLine;
  // color overrides (urgency, bus identity); fall back to kind color.
  const resolved = color ?? (isPickup ? "info.700" : "success.700");
  return (
    <Box
      component="span"
      aria-label={isPickup ? "Nouto" : "Toimitus"}
      sx={{
        display: "inline-flex",
        color: resolved,
      }}
    >
      <Icon size={16} strokeWidth={2.5} />
    </Box>
  );
}

function Chevron({ open }) {
  return (
    <Typography
      sx={{
        color: "text.disabled",
        fontSize: 16,
        lineHeight: 1,
        transition: "transform 0.2s",
        transform: open ? "rotate(90deg)" : "none",
        display: "inline-block",
      }}
    >
      ›
    </Typography>
  );
}

function AmountCell({ count, bus = false }) {
  return (
    <Box component="td" sx={{ ...CELL, ...COL.amount, textAlign: "center" }}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          fontVariantNumeric: "tabular-nums",
          color: bus ? "warning.800" : "text.secondary",
        }}
      >
        {count}
      </Typography>
    </Box>
  );
}

function DateDivider({ label, colSpan }) {
  return (
    <Box component="tr">
      <Box
        component="td"
        colSpan={colSpan}
        sx={{
          p: 0,
          border: "none !important",
          borderBottom: "0.5px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// Local day key (YYYY-MM-DD) — compared with === to detect day transitions.
function dayKey(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Tänään / Huomenna / Ylihuomenna / "Pe 15.5."
function dateLabel(date) {
  const today    = new Date();
  const todayKey = dayKey(today);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const dayAfter = new Date(today); dayAfter.setDate(today.getDate() + 2);

  const k = dayKey(date);
  if (k === todayKey)            return "Tänään";
  if (k === dayKey(tomorrow))    return "Huomenna";
  if (k === dayKey(dayAfter))    return "Ylihuomenna";

  return date.toLocaleDateString("fi-FI", {
    weekday: "short",
    day:     "numeric",
    month:   "numeric",
  });
}

// ─── shared cell/row sx ───────────────────────────────────────────────────────

const CELL = {
  px: 1.25, py: 1,
  borderBottom: "0.5px solid",
  borderColor: "divider",
  verticalAlign: "middle",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

const COL = {
  time:   { width: 68 },
  type:   { width: 64 },
  info:   { maxWidth: 0 },
  amount: { width: 56 },
  go:     { width: 28 },
};

function rowSx(selected, bus = false, completing = false, stale = false) {
  const base = {
    cursor: completing ? "default" : "pointer",
    transition: "background-color 0.1s, opacity 0.2s",
    pointerEvents: completing ? "none" : "auto",
    opacity: stale ? 0.55 : 1,
  };
  if (selected && completing) return { ...base, "& td": { bgcolor: "info.50", opacity: 0.5, transition: "opacity 0.3s" } };
  if (selected) return { ...base, "& td": { bgcolor: "info.50" } };
  if (bus)      return { ...base, "& td": { bgcolor: "warning.50" }, "&:hover td": { bgcolor: "warning.100" } };
  return { ...base, "&:hover td": { bgcolor: "action.hover" } };
}

// ─── expand panels ────────────────────────────────────────────────────────────

function ExpandSingle({ group, navigate }) {
  const action = group.actions[0];
  return (
    <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap>{group.info.primary}</Typography>
        {group.info.secondary && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {group.info.secondary}
          </Typography>
        )}
        <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.tertiary" }}>
          {action.deliveryId}
        </Typography>
      </Box>
      <MapButton address={group.info.primary} />
      <Button
        size="small"
        variant="outlined"
        onClick={() => navigate(`/kuljeta-paketti?deliveryId=${action.deliveryId}`)}
        sx={{ flexShrink: 0, fontSize: "0.75rem" }}
      >
        Avaa ↗
      </Button>
    </Box>
  );
}

function ExpandGroup({ group, navigate }) {
  const isPickup = group.kind === "pickup";
  return (
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {group.actions.length} pakettia {isPickup ? "noudettavana" : "toimitettavana"} samasta
        osoitteesta — kaikki merkitään suoritetuiksi kerralla.
      </Typography>
      {group.actions.map((action) => (
        <Box
          key={action.deliveryId}
          onClick={() => navigate(`/kuljeta-paketti?deliveryId=${action.deliveryId}`)}
          sx={{
            display: "flex", alignItems: "center", gap: 1.25,
            p: 1, border: "0.5px solid", borderColor: "divider",
            borderRadius: 1.5, cursor: "pointer",
            transition: "background-color 0.1s",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {action.info.secondary || action.info.primary}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.tertiary" }}>
              {action.deliveryId}
            </Typography>
          </Box>
          <MapButton address={action.info.primary} />
          <Typography sx={{ color: "text.disabled", fontSize: 16 }}>›</Typography>
        </Box>
      ))}
    </Box>
  );
}

function ExpandBus({ group, navigate }) {
  const action = group.actions[0];
  const bus    = action.busInfo;

  if (!bus) return <ExpandSingle group={group} navigate={navigate} />;

  return (
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>

      {/* Bus badge */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            fontFamily: "monospace",
            px: 1, py: 0.25, borderRadius: 0.75,
            bgcolor: "warning.50", color: "warning.800",
            border: "0.5px solid", borderColor: "warning.300",
          }}
        >
          {bus.shortName}
        </Typography>
        <Typography variant="body2" fontWeight={500}>{bus.exactName}</Typography>
      </Box>

      {/* Route */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">{bus.origin}</Typography>
          <Typography variant="caption" color="text.disabled">→</Typography>
          <Typography variant="caption" color="text.secondary">{bus.destination}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtTime(bus.start)} → {fmtTime(bus.end)}
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
        <BusTrackButton />
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate(`/kuljeta-paketti?deliveryId=${action.deliveryId}`)}
          sx={{ fontSize: "0.75rem" }}
        >
          Avaa ↗
        </Button>
      </Box>
    </Box>
  );
}

function ExpandRow({ group, open, navigate, completing, colSpan }) {
  if (!open) return null;
  const content = group.isBus
    ? <ExpandBus   group={group} navigate={navigate} />
    : group.actions.length > 1
    ? <ExpandGroup group={group} navigate={navigate} />
    : <ExpandSingle group={group} navigate={navigate} />;

  return (
    <Box component="tr">
      <Box
        component="td"
        colSpan={colSpan}
        sx={{
          p: 0,
          borderBottom: "0.5px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {completing && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: 2,
              borderRadius: 0,
            }}
          />
        )}
        <Box sx={{ opacity: completing ? 0.4 : 1, transition: "opacity 0.2s", pointerEvents: completing ? "none" : "auto" }}>
          {content}
        </Box>
      </Box>
    </Box>
  );
}

// ─── table rows ───────────────────────────────────────────────────────────────

function BusRow({ group, open, onToggle, stale = false, anchor = false, showAmount = false }) {
  const action = group.actions[0];
  const bus    = action.busInfo;

  return (
    <Box component="tr" sx={rowSx(open, true, false, stale)} onClick={onToggle} {...(anchor ? { "data-now-anchor": true } : {})}>
      <Box component="td" sx={{ ...CELL, ...COL.time }}>
        <Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums", color: "warning.800" }}>
          {fmtTime(group.time)}
        </Typography>
      </Box>
      <Box component="td" sx={{ ...CELL, ...COL.type }}>
        <KindPill kind={group.kind} color={urgencyColor(group.time)} />
      </Box>
      <Box component="td" sx={{ ...CELL, ...COL.info }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace", fontWeight: 700, display: "inline-block",
            px: 0.625, py: 0.125, borderRadius: 0.5,
            bgcolor: "warning.100", color: "warning.800",
            border: "0.5px solid", borderColor: "warning.300",
          }}
        >
          {group.info.primary}
        </Typography>
        {group.info.secondary && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {group.info.secondary}
          </Typography>
        )}
      </Box>
      {showAmount && <AmountCell count={group.actions.length} bus />}
      <Box component="td" sx={{ ...CELL, ...COL.go }}><Chevron open={open} /></Box>
    </Box>
  );
}

function GroupHeaderRow({ group, open, onToggle, completing = false, stale = false, anchor = false, showAmount = false }) {
  return (
    <Box
      component="tr"
      sx={{
        cursor: completing ? "default" : "pointer",
        pointerEvents: completing ? "none" : "auto",
        transition: "background-color 0.1s, opacity 0.2s",
        opacity: stale ? 0.55 : 1,
        "& td": { bgcolor: open ? "info.50" : "action.hover", opacity: completing ? 0.5 : 1, transition: "opacity 0.3s" },
        "&:hover td": { bgcolor: open ? "info.50" : "action.selected" },
      }}
      onClick={onToggle}
      {...(anchor ? { "data-now-anchor": true } : {})}
    >
      <Box component="td" sx={{ ...CELL, ...COL.time }}>
        <Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtTime(group.time)}
        </Typography>
        {group.windowEnd && (
          <Typography variant="caption" color="text.tertiary" sx={{ fontVariantNumeric: "tabular-nums" }}>
            – {fmtTime(group.windowEnd)}
          </Typography>
        )}
      </Box>
      <Box component="td" sx={{ ...CELL, ...COL.type }}>
        <KindPill kind={group.kind} color={urgencyColor(group.time)} />
      </Box>
      <Box component="td" sx={{ ...CELL, ...COL.info }}>
        <Typography variant="body2" fontWeight={500} noWrap>{group.info.primary}</Typography>
        {group.info.secondary && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {group.info.secondary}
          </Typography>
        )}
      </Box>
      {showAmount && <AmountCell count={group.actions.length} />}
      <Box component="td" sx={{ ...CELL, ...COL.go }}><Chevron open={open} /></Box>
    </Box>
  );
}

function FlatRow({ group, open, onToggle, completing = false, stale = false, anchor = false, showAmount = false }) {
  return (
    <Box component="tr" sx={rowSx(open, false, completing, stale)} onClick={onToggle} {...(anchor ? { "data-now-anchor": true } : {})}>
      <Box component="td" sx={{ ...CELL, ...COL.time }}>
        <Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtTime(group.time)}
        </Typography>
        {group.windowEnd && (
          <Typography variant="caption" color="text.tertiary" sx={{ fontVariantNumeric: "tabular-nums" }}>
            – {fmtTime(group.windowEnd)}
          </Typography>
        )}
      </Box>
      <Box component="td" sx={{ ...CELL, ...COL.type }}>
        <KindPill kind={group.kind} color={urgencyColor(group.time)} />
      </Box>
      <Box component="td" sx={{ ...CELL, ...COL.info }}>
        <Typography variant="body2" fontWeight={500} noWrap>{group.info.primary}</Typography>
        {group.info.secondary && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {group.info.secondary}
          </Typography>
        )}
      </Box>
      {showAmount && <AmountCell count={group.actions.length} />}
      <Box component="td" sx={{ ...CELL, ...COL.go }}><Chevron open={open} /></Box>
    </Box>
  );
}

// ─── empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: { xs: 6, sm: 10 }, gap: 1.5, textAlign: "center" }}>
      <Box sx={{ width: 56, height: 56, borderRadius: "16px", bgcolor: "action.hover", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Clock size={24} strokeWidth={1.5} style={{ opacity: 0.35 }} />
      </Box>
      <Typography variant="subtitle1" fontWeight={700}>Ei toimintoja</Typography>
      <Typography variant="body2" color="text.secondary">
        Ei kuljetuksia muodostettavaksi toiminnoiksi.
      </Typography>
    </Box>
  );
}

// ─── ActionList ───────────────────────────────────────────────────────────────

export function ActionList({ deliveries = [] }) {
  const navigate = useNavigate();
  const { markAsPicked, completeDelivery } = useDelivery();
  const [grouped,    setGrouped]    = useState(true);
  const [openKey,    setOpenKey]    = useState(null);
  const [completing, setCompleting] = useState(false);

  const [reportOpen, setReportOpen] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const handleReportSubmit = async ({ deliveryId, issueType, issueLabel, details }) => {
    setReportSubmitting(true);
    try {
      const functions = getFunctions();
      const reportDeliveryProblem = httpsCallable(functions, "reportDeliveryProblem");
      await reportDeliveryProblem({ deliveryId, issueType, issueLabel, details });
      setReportOpen(false);
    } catch (err) {
      console.error("Failed to report problem", err);
      alert("Ilmoituksen lähetys epäonnistui. Yritä uudelleen tai soita +358 50 505 8167.");
    } finally {
      setReportSubmitting(false);
    }
  };
  const scrollRef = useRef(null);

  const actions = useMemo(() => toActions(deliveries), [deliveries]);
  const groups  = useMemo(
    () => grouped
      ? groupActions(actions)
      : actions.map((a) => ({ ...a, isBus: !!a.busInfo, actions: [a] })),
    [actions, grouped]
  );

  // Auto-scroll to first non-stale row on mount + when grouping toggles.
  // Re-running on `groups` covers both, since groups changes on either.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const anchor = scroller.querySelector("[data-now-anchor]");
    if (!anchor) return;

    // Position anchor ~2 rows from top of the scroll viewport.
    const offset    = 2 * 44; // ~row height
    const anchorTop = anchor.offsetTop - scroller.offsetTop;
    scroller.scrollTop = Math.max(0, anchorTop - offset);
  }, [groups]);

  const toggle = useCallback((key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  const handleComplete = useCallback(async () => {
    if (!openKey || completing) return;
    const openGroup = groups.find((g) => groupKey(g) === openKey);
    if (!openGroup) return;

    setCompleting(true);
    try {
      const { kind, actions: groupActions_ } = openGroup;
      const pickups  = kind === "pickup"  ? groupActions_ : [];
      const delivers = kind === "deliver" ? groupActions_ : [];

      for (const a of pickups)  await markAsPicked(a.deliveryId);
      for (const a of delivers) await completeDelivery(a.deliveryId);

      setOpenKey(null);
    } catch (err) {
      console.error("Suoritettu failed:", err);
    } finally {
      setCompleting(false);
    }
  }, [openKey, completing, groups, markAsPicked, completeDelivery]);

  if (!actions.length) return <EmptyState />;

  const openGroup = openKey ? groups.find((g) => groupKey(g) === openKey) : null;
  const openCount = openGroup?.actions.length ?? 0;

  // Column count: time, type, info, (amount), go.
  const totalCols = grouped ? 5 : 4;

  const rows = [];
  let lastDay = null;
  const nowMs  = Date.now();
  let anchorAssigned = false;

  for (const group of groups) {
    const day = dayKey(group.time);
    if (day && day !== lastDay) {
      rows.push(<DateDivider key={`day-${day}`} label={dateLabel(group.time)} colSpan={totalCols} />);
      lastDay = day;
    }

    const key      = groupKey(group);
    const open     = openKey === key;
    const rowDoing = open && completing;
    const stale    = group.time && group.time.getTime() < nowMs;
    const anchor   = !anchorAssigned && !stale && !!group.time;
    if (anchor) anchorAssigned = true;

    if (group.isBus) {
      rows.push(<BusRow         key={key} group={group} open={open} onToggle={() => toggle(key)} stale={stale} anchor={anchor} showAmount={grouped} />);
    } else if (grouped && group.actions.length > 1) {
      rows.push(<GroupHeaderRow key={key} group={group} open={open} onToggle={() => toggle(key)} completing={rowDoing} stale={stale} anchor={anchor} showAmount={grouped} />);
    } else {
      rows.push(<FlatRow        key={key} group={group} open={open} onToggle={() => toggle(key)} completing={rowDoing} stale={stale} anchor={anchor} showAmount={grouped} />);
    }

    rows.push(<ExpandRow key={`${key}-expand`} group={group} open={open} navigate={navigate} completing={rowDoing} colSpan={totalCols} />);
  }

  return (
    <Box>
      <TodayStatsPanel actions={actions} />

      {/* Table */}
      <Box
        ref={scrollRef}
        sx={{
          maxHeight: { xs: "calc(100vh - 220px)", sm: "70vh" },
          overflowY: "auto",
          overflowX: "hidden",
          border: "0.5px solid",
          borderColor: "divider",
          borderRadius: 2,
          // Keep the inner table corners flush with the rounded container
          "& > table": { borderRadius: 0, border: "none" },
        }}
      >
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <Box
            component="thead"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              bgcolor: "background.paper",
            }}
          >
            <Box component="tr" sx={{ "& th": { borderBottom: "0.5px solid", borderColor: "divider" } }}>
              {[
                ["time",   "Aika",     COL.time],
                ["type",   "Toiminto", COL.type],
                ["info",   "Tieto",    COL.info],
                ...(grouped ? [["amount", "Määrä", { ...COL.amount, textAlign: "center" }]] : []),
                ["go",     "",         COL.go],
              ].map(([id, label, sx]) => (
                <Box
                  key={id}
                  component="th"
                  sx={{
                    px: 1.25, py: 0.875, textAlign: "left",
                    fontSize: "0.68rem", fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase", letterSpacing: 0.5,
                    bgcolor: "background.paper",
                    ...sx,
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">{rows}</Box>
        </Box>
      </Box>

      {/* Bottom bar */}
      <ActionListFooter
        grouped={grouped}
        onGroupedChange={(checked) => {
          setGrouped(checked);
          setOpenKey(null);
        }}
        openKey={openKey}
        openCount={openCount}
        completing={completing}
        onComplete={handleComplete}
        onReportProblem={() => setReportOpen(true)}
      />
      <ReportProblemModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmit={handleReportSubmit}
        deliveryId={openKey}
        submitting={reportSubmitting}
      />
    </Box>
  );
}